import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import {
  AiFillGithub,
  AiOutlineTwitter,
  AiFillInstagram,
  AiOutlineMail,
} from "react-icons/ai";
import { FaLinkedinIn } from "react-icons/fa";
import ScrollReveal from "../ScrollReveal/ScrollReveal";
import useScrollAnimation from "./useScrollAnimation";
import "./home2.css";

const values = [
  {
    title: "Backend",
    text: "TypeScript, Node.js, NestJS, REST APIs, modular architecture, queues, background jobs, and event-driven workflows.",
  },
  {
    title: "Integrations",
    text: "Webhook handlers, retries, idempotency, rate limits, audit logs, and secure secrets management.",
  },
  {
    title: "Security",
    text: "Auth guards, token-based auth, OAuth2/OIDC familiarity, secure data handling, encryption basics, and production discipline.",
  },
  {
    title: "Databases",
    text: "PostgreSQL, MySQL, schema design, indexing, query optimization, and atomic transactions.",
  },
  {
    title: "DevOps",
    text: "Docker, CI/CD basics, environment management, monitoring and logging fundamentals, with familiarity around GCP concepts.",
  },
  {
    title: "Also",
    text: "Flutter, Dart, Firebase, and close collaboration across product, QA, and engineering teams.",
  },
];

const socials = [
  {
    href: "https://github.com/shankarkharel",
    label: "github",
    icon: <AiFillGithub />,
  },
  {
    href: "https://twitter.com/shankarkharel19",
    label: "twitter",
    icon: <AiOutlineTwitter />,
  },
  {
    href: "mailto:itsshankar35@gmail.com",
    label: "email",
    icon: <AiOutlineMail />,
  },
  {
    href: "https://www.linkedin.com/in/shankar-kharel-162846168/",
    label: "linkedin",
    icon: <FaLinkedinIn />,
  },
  {
    href: "https://www.instagram.com/shankarkharel07",
    label: "instagram",
    icon: <AiFillInstagram />,
  },
];

const summarySnippet = [
  "pipeline {",
  "  design: ['REST APIs', 'modular services'],",
  "  reliability: ['retries', 'idempotency', 'audit logs'],",
  "  security: ['guards', 'tokens', 'safe data handling'],",
  "  delivery: 'architecture -> deployment -> support',",
  "}",
];

const summarySignals = [
  {
    label: "Production engineering",
    value: "5+ years building backend systems for mobile and web products.",
  },
  {
    label: "Stack depth",
    value: "Node.js, TypeScript, NestJS, PostgreSQL, MySQL, Docker, and CI/CD basics.",
  },
  {
    label: "Leadership scope",
    value: "Requirements, architecture, implementation, deployment, and production support.",
  },
];

function Home2() {
  const isSummaryVisible = useScrollAnimation(".summary-trigger");

  return (
    <Container fluid className="home-about-section" id="about">
      <Container className="section-shell">
        <Row className="align-items-center section-gap">
          <Col lg={6}>
            <ScrollReveal distance={28}>
              <div className="section-intro">
                <span className="section-kicker">Summary</span>
                <h2>Backend-focused engineering with team leadership experience.</h2>
                <p className="section-copy">
                  Experienced in building production backend systems for mobile
                  and web products, leading teams, and owning delivery from
                  architecture to deployment and production support. Strongest in
                  backend systems, integrations, and stable platform workflows.
                </p>
              </div>
              <div className="value-grid">
                {values.map((item) => (
                  <ScrollReveal
                    as="article"
                    className="value-card"
                    key={item.title}
                    delay={values.indexOf(item) * 70}
                    distance={18}
                  >
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </ScrollReveal>
                ))}
              </div>
            </ScrollReveal>
          </Col>

          <Col lg={6}>
            <ScrollReveal
              className={`summary-trigger summary-rail ${isSummaryVisible ? "active" : ""}`}
              variant="left"
              delay={90}
              distance={34}
            >
              <div className="summary-rail-head">
                <span className="section-kicker">Current Focus</span>
                <p>
                  Building reliable backend platforms with clean contracts,
                  secure integrations, and stable delivery practices.
                </p>
              </div>
              <pre className="summary-snippet">
                <code>{summarySnippet.join("\n")}</code>
              </pre>
              <div className="summary-signal-list">
                {summarySignals.map((item) => (
                  <ScrollReveal
                    as="article"
                    className="summary-signal"
                    key={item.label}
                    delay={120 + summarySignals.indexOf(item) * 80}
                    distance={18}
                  >
                    <span>{item.label}</span>
                    <p>{item.value}</p>
                  </ScrollReveal>
                ))}
              </div>
            </ScrollReveal>
          </Col>
        </Row>

        <ScrollReveal as="section" className="connect-panel" delay={120} distance={24}>
          <div className="section-intro compact center">
            <span className="section-kicker">Connect</span>
            <h2>Open to backend engineering, platform work, and technical leadership roles.</h2>
          </div>
          <ul className="home-about-social-links">
            {socials.map((item) => (
              <ScrollReveal
                as="li"
                className="social-icons"
                key={item.label}
                delay={140 + socials.indexOf(item) * 60}
                distance={14}
              >
                <a
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  className="home-social-icons"
                  aria-label={item.label}
                >
                  {item.icon}
                </a>
              </ScrollReveal>
            ))}
          </ul>
        </ScrollReveal>
      </Container>
    </Container>
  );
}

export default Home2;
